import type { ComponentProps, ReactNode } from "react"
import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useDebounce } from "react-use"

import type { PainLevel } from "@productlane/db/browser"
import type { FeedbackWithRelations } from "@productlane/zero/queries"
import { EmailType } from "@productlane/db/browser"
import { generateId } from "@productlane/lib/src/browser"
import {
	Avatar,
	ContextMenuItem,
	ContextMenuSub,
	ContextMenuSubContent,
	ContextMenuSubTrigger,
	IssueStatusIcon,
	PriorityIcons,
	PriorityNaming,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { CopyOption } from "./thread-menu-model"
import type {
	IssueSelectIssue,
	ProjectSelectProject,
} from "@/screens/app/threads/components/right-bar/issue-select"
import { toast } from "@/components/toast"
import { generateId as generateProjectId } from "@/lib/id"
import { getProjectIcon } from "@/lib/projects"
import { matchesSearch } from "@/lib/search-match"
import { mutateOk, useZero } from "@/lib/zero"
import {
	formatSnoozeTime,
	getSnoozePresets,
} from "@/screens/app/threads/components/center-content/snooze-options"
import { pickSuggestions } from "@/screens/app/threads/components/right-bar/shared/picker-suggestions"
import { useSuggestedLinks } from "@/screens/app/threads/components/right-bar/thread-link-suggestions"
import { useIssueProjectSearch } from "@/screens/app/threads/components/right-bar/use-issue-project-search"
import {
	customerNeedBody,
	issueLinkArgs,
	projectLinkArgs,
} from "@/screens/app/threads/lib/link-args"
import { MenuOption, SearchSubContent } from "./menu-search"
import { THREAD_MENU_COPY as copy } from "./thread-menu-model"

export type MenuThread = FeedbackWithRelations

type TriggerProps = Omit<
	ComponentProps<typeof ContextMenuSubTrigger>,
	"children"
>

/** The submenu body mounts only while it is open, so its queries run only then. */
export function LazySub({
	label,
	trigger,
	children,
}: {
	label: ReactNode
	trigger: TriggerProps & { shortcutKey?: string }
	children: () => ReactNode
}) {
	const [open, setOpen] = useState(false)
	const { shortcutKey, ...triggerProps } = trigger
	return (
		<ContextMenuSub open={open} onOpenChange={setOpen}>
			<ContextMenuSubTrigger {...triggerProps} data-shortcut={shortcutKey}>
				{label}
			</ContextMenuSubTrigger>
			{open ? children() : null}
		</ContextMenuSub>
	)
}

// Long lists render their best matches; the field narrows the rest.
const MAX_ROWS = 50
const PRIORITY_LEVELS: PainLevel[] = ["UNKNOWN", "HIGH", "MEDIUM", "LOW"]

export function PriorityContent({
	painLevel,
	onSelect,
}: {
	painLevel: PainLevel | null
	onSelect: (level: PainLevel) => void
}) {
	const [query, setQuery] = useState("")
	const levels = PRIORITY_LEVELS.filter((level) =>
		matchesSearch(query, PriorityNaming[level]),
	)
	return (
		<SearchSubContent
			placeholder={copy.search.priority}
			query={query}
			onQueryChange={setQuery}
			isEmpty={levels.length === 0}
			emptyLabel={copy.empty.priority}
			className="w-56"
		>
			{levels.map((level) => {
				const Icon = PriorityIcons[level]
				return (
					<MenuOption
						key={level}
						leading={<Icon className="h-4 w-4 flex-none" />}
						label={PriorityNaming[level]}
						selected={painLevel === level}
						onSelect={() => onSelect(level)}
					/>
				)
			})}
		</SearchSubContent>
	)
}

export function AssigneeContent({
	assigneeId,
	onSelect,
}: {
	assigneeId: string | null | undefined
	onSelect: (userId: string | null) => void
}) {
	const [query, setQuery] = useState("")
	const [teamMembers] = useQuery(queries.teamMembers())
	const members = useMemo(
		() =>
			teamMembers.filter(
				(member) =>
					member.user &&
					matchesSearch(query, member.user.name, member.user.email),
			),
		[teamMembers, query],
	)
	const showUnassign = !!assigneeId && matchesSearch(query, copy.unassign)
	return (
		<SearchSubContent
			placeholder={copy.search.assignee}
			query={query}
			onQueryChange={setQuery}
			isEmpty={members.length === 0 && !showUnassign}
			emptyLabel={copy.empty.assignee}
		>
			{showUnassign && (
				<MenuOption label={copy.unassign} onSelect={() => onSelect(null)} />
			)}
			{members.slice(0, MAX_ROWS).map((member) => (
				<MenuOption
					key={member.id}
					leading={
						<Avatar
							image={member.user?.imageUrl}
							name={member.user?.name ?? member.user?.email ?? ""}
							size="xs"
							roundness="full"
						/>
					}
					label={member.user?.name || member.user?.email}
					selected={assigneeId === member.userId}
					onSelect={() => onSelect(member.userId)}
				/>
			))}
		</SearchSubContent>
	)
}

export function TagsContent({ thread }: { thread: MenuThread }) {
	const zero = useZero()
	const [query, setQuery] = useState("")
	const [tags] = useQuery(queries.searchTags({ query, limit: 100 }))
	const [tagGroups] = useQuery(queries.tagGroups())
	const linked = new Set((thread.tags ?? []).map((tag) => tag.id))
	const groups = tagGroups
		.map((group) => ({
			group,
			tags: tags.filter((tag) => tag.tagGroupId === group.id),
		}))
		.filter((entry) => entry.tags.length > 0)

	const toggle = (tagId: string) => {
		mutateOk(zero.mutate(mutators.tags.linkTag({ threadId: thread.id, tagId })))
			.then(() => removeMarks(tagId, linked.has(tagId)))
			.catch(() => toast.error({ title: copy.failed }))
	}

	return (
		<SearchSubContent
			placeholder={copy.search.tags}
			query={query}
			onQueryChange={setQuery}
			isEmpty={groups.length === 0}
			emptyLabel={copy.empty.tags}
		>
			{groups.map(({ group, tags: groupTags }) => (
				<div key={group.id} role="group" aria-label={group.name}>
					<div
						className="px-2 pb-1 pt-2 text-xs font-medium"
						style={{ color: group.color }}
					>
						{group.name}
					</div>
					{groupTags.map((tag) => {
						const TagIcon = getProjectIcon(tag.icon)
						return (
							<MenuOption
								key={tag.id}
								leading={
									<TagIcon
										className="h-3 w-3 flex-none"
										style={{ color: group.color }}
									/>
								}
								label={tag.name}
								selected={linked.has(tag.id)}
								// Stays open so several tags go on in one pass, like Linear's labels.
								onSelect={(event) => {
									event.preventDefault()
									toggle(tag.id)
								}}
							/>
						)
					})}
				</div>
			))}
		</SearchSubContent>
	)
}

// Unlinking leaves the editor's highlight marks behind unless they are unwrapped too.
function removeMarks(id: string, wasLinked: boolean) {
	if (!wasLinked) return
	document.querySelectorAll(`mark[data-id="${id}"]`).forEach((mark) => {
		mark.replaceWith(mark.innerHTML)
	})
}

type IssueRow =
	| { kind: "issue"; item: IssueSelectIssue }
	| { kind: "project"; item: ProjectSelectProject }

const NO_ISSUES: MenuThread["issues"] = []
const NO_PROJECTS: MenuThread["projects"] = []

export function IssueProjectContent({ thread }: { thread: MenuThread }) {
	const zero = useZero()
	const [query, setQuery] = useState("")
	// A list row can arrive before Zero has hydrated its relations.
	const threadIssues = thread.issues ?? NO_ISSUES
	const threadProjects = thread.projects ?? NO_PROJECTS
	const linkedIssues = useMemo(
		() => new Map(threadIssues.map((issue) => [issue.linearTicketId, issue])),
		[threadIssues],
	)
	const linkedProjects = useMemo(
		() => new Set(threadProjects.map((project) => project.id)),
		[threadProjects],
	)
	const linkedIssueIds = useMemo(
		() => threadIssues.map((issue) => issue.id),
		[threadIssues],
	)
	const linkedProjectIds = useMemo(() => [...linkedProjects], [linkedProjects])
	const suggestedLinks = useSuggestedLinks({
		threadId: thread.id,
		origin: thread.origin,
		linkedIssueIds,
		linkedProjectIds,
	})
	const { selectedTeamIds, issues, projects, suggested } =
		useIssueProjectSearch({
			searchQuery: query,
			issueIds: linkedIssueIds,
			projectIds: linkedProjectIds,
			showProjects: true,
			suggested: suggestedLinks,
			relatedTo: thread,
		})
	// The customer need quotes the opening message, which only the oldest-rows query holds.
	const [oldest] = useQuery(
		queries.threadOldestMessages({ threadId: thread.id }),
		{ ttl: "10m" },
	)

	const rows = useMemo<IssueRow[]>(() => {
		if (query.trim()) {
			return [
				...projects.map((item) => ({ kind: "project" as const, item })),
				...issues.map((item) => ({ kind: "issue" as const, item })),
			].slice(0, MAX_ROWS)
		}
		const linked: IssueRow[] = [
			...threadProjects.map((project) => ({
				kind: "project" as const,
				item: { ...project, type: "local" as const, isSelected: true },
			})),
			...threadIssues.map((issue) => ({
				kind: "issue" as const,
				item: { ...issue, type: "local" as const, isSelected: true },
			})),
		]
		const suggestions = pickSuggestions(
			issues.filter((issue) => !linkedIssues.has(issue.linearTicketId)),
			projects.filter((project) => !linkedProjects.has(project.id)),
			8,
			suggested,
		)
		return [...linked, ...suggestions]
	}, [
		query,
		issues,
		projects,
		threadIssues,
		threadProjects,
		linkedIssues,
		linkedProjects,
		suggested,
	])

	const body = () =>
		customerNeedBody({
			text: thread.text,
			origin: thread.origin,
			firstInboundEmail: oldest?.emails[0],
			latestOutboundEmail: thread.emails.find(
				(email) => email.type === EmailType.OUTBOUND,
			),
			firstSlackMessage: (oldest?.slackMessages ?? thread.slackMessages).find(
				(message) => message.type === EmailType.INBOUND,
			),
			latestSlackMessage: thread.slackMessages.find(
				(message) => message.type === EmailType.OUTBOUND,
			),
		})

	const run = (promise: Promise<unknown>) =>
		promise.catch(() => toast.error({ title: copy.failed }))

	const toggleIssue = (issue: IssueSelectIssue) => {
		const existing = linkedIssues.get(issue.linearTicketId)
		if (existing) {
			removeMarks(existing.linearTicketId, true)
			void run(
				mutateOk(
					zero.mutate(
						mutators.issues.unlink({
							feedbackId: thread.id,
							internalIssueId: existing.id,
						}),
					),
				),
			)
			return
		}
		void run(
			mutateOk(
				zero.mutate(
					mutators.issues.link(
						issueLinkArgs({
							thread,
							issue,
							body: body(),
							newIssueId: generateId(),
							visibility: { notUpdateVisibility: true },
						}),
					),
				),
			),
		)
	}

	const toggleProject = (project: ProjectSelectProject) => {
		if (linkedProjects.has(project.id)) {
			removeMarks(project.id, true)
			void run(
				mutateOk(
					zero.mutate(
						mutators.projects.unlink({
							feedbackId: thread.id,
							internalProjectId: project.id,
						}),
					),
				),
			)
			return
		}
		void run(
			mutateOk(
				zero.mutate(
					mutators.projects.link(
						projectLinkArgs({
							thread,
							project,
							body: body(),
							newProjectId: generateProjectId(),
							visibility: { notUpdateVisibility: true },
						}),
					),
				),
			),
		)
	}

	const noTeams = selectedTeamIds.length === 0

	return (
		<SearchSubContent
			placeholder={copy.search.issueOrProject}
			query={query}
			onQueryChange={setQuery}
			isEmpty={noTeams || rows.length === 0}
			emptyLabel={noTeams ? copy.empty.linearTeams : copy.empty.issueOrProject}
			className="w-80"
		>
			{!noTeams &&
				rows.map((row) =>
					row.kind === "issue" ? (
						<MenuOption
							key={`issue-${row.item.linearTicketId}`}
							leading={
								<IssueStatusIcon
									className="h-3.5 w-3.5 flex-none"
									color={row.item.statusColor ?? undefined}
									status={row.item.status ?? undefined}
								/>
							}
							label={row.item.title}
							detail={row.item.identifier}
							selected={linkedIssues.has(row.item.linearTicketId)}
							onSelect={() => toggleIssue(row.item)}
						/>
					) : (
						<ProjectOption
							key={`project-${row.item.id}`}
							project={row.item}
							selected={linkedProjects.has(row.item.id)}
							onSelect={() => toggleProject(row.item)}
						/>
					),
				)}
		</SearchSubContent>
	)
}

function ProjectOption({
	project,
	selected,
	onSelect,
}: {
	project: ProjectSelectProject
	selected: boolean
	onSelect: () => void
}) {
	const Icon = getProjectIcon(project.icon)
	return (
		<MenuOption
			leading={
				<Icon
					className="h-3.5 w-3.5 flex-none"
					style={{ color: project.color ?? undefined }}
				/>
			}
			label={project.name}
			selected={selected}
			onSelect={onSelect}
		/>
	)
}

function useDebouncedQuery(query: string) {
	const [debounced, setDebounced] = useState(query)
	useDebounce(() => setDebounced(query), 200, [query])
	return debounced
}

export function PersonContent({
	contactId,
	onSelect,
}: {
	contactId: string | null | undefined
	onSelect: (contact: { id: string; companyId?: string | null } | null) => void
}) {
	const [query, setQuery] = useState("")
	const search = useDebouncedQuery(query)
	// Searched and capped on the server; the workspace can hold hundreds of thousands of people.
	const [contacts] = useQuery(
		queries.contactOptions({ query: search, limit: MAX_ROWS }),
	)
	const showClear = !!contactId && matchesSearch(query, copy.noPerson)
	return (
		<SearchSubContent
			placeholder={copy.search.person}
			query={query}
			onQueryChange={setQuery}
			isEmpty={contacts.length === 0 && !showClear}
			emptyLabel={copy.empty.person}
			className="w-72"
		>
			{showClear && (
				<MenuOption label={copy.noPerson} onSelect={() => onSelect(null)} />
			)}
			{contacts.map((contact) => (
				<MenuOption
					key={contact.id}
					leading={
						<Avatar
							image={contact.imageUrl}
							name={contact.name || contact.email}
							colorSeed={contact.email}
							size="xs"
						/>
					}
					label={contact.name || contact.email}
					detail={contact.name ? contact.email : undefined}
					selected={contactId === contact.id}
					onSelect={() => onSelect(contact)}
				/>
			))}
		</SearchSubContent>
	)
}

export function CustomerContent({
	companyId,
	onSelect,
}: {
	companyId: string | null | undefined
	onSelect: (companyId: string | null) => void
}) {
	const [query, setQuery] = useState("")
	const search = useDebouncedQuery(query)
	const [companies] = useQuery(
		queries.companyOptions({ query: search, limit: MAX_ROWS }),
	)
	const showClear = !!companyId && matchesSearch(query, copy.noCustomer)
	return (
		<SearchSubContent
			placeholder={copy.search.customer}
			query={query}
			onQueryChange={setQuery}
			isEmpty={companies.length === 0 && !showClear}
			emptyLabel={copy.empty.customer}
			className="w-72"
		>
			{showClear && (
				<MenuOption label={copy.noCustomer} onSelect={() => onSelect(null)} />
			)}
			{companies.map((company) => (
				<MenuOption
					key={company.id}
					leading={
						<Avatar image={company.logoUrl} name={company.name} size="xs" />
					}
					label={company.name}
					selected={companyId === company.id}
					onSelect={() => onSelect(company.id)}
				/>
			))}
		</SearchSubContent>
	)
}

export function SnoozeContent({
	onSelect,
	onPickDate,
}: {
	onSelect: (value: Date) => void
	onPickDate: () => void
}) {
	// Read once per open, so "Later today" means three hours from now, not from page load.
	const [presets] = useState(() => getSnoozePresets())
	return (
		<ContextMenuSubContent className="w-64" sideOffset={4} alignOffset={-5}>
			{presets.map((preset) => (
				<ContextMenuItem
					key={preset.label}
					shortcut={formatSnoozeTime(preset.value)}
					onSelect={() => onSelect(preset.value)}
				>
					{preset.label}
				</ContextMenuItem>
			))}
			<ContextMenuItem onSelect={onPickDate}>
				{copy.snoozeCustom}
			</ContextMenuItem>
		</ContextMenuSubContent>
	)
}

export function CopyContent({
	options,
	onCopy,
}: {
	options: CopyOption[]
	onCopy: (option: CopyOption) => void
}) {
	return (
		<ContextMenuSubContent className="w-60" sideOffset={4} alignOffset={-5}>
			{options.map((option) => (
				<ContextMenuItem key={option.key} onSelect={() => onCopy(option)}>
					{option.label}
				</ContextMenuItem>
			))}
		</ContextMenuSubContent>
	)
}
